import { registerLoaders } from "@loaders.gl/core";
import * as Sentry from "@sentry/react";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import { CookieBanner } from "./components/CookieBanner";
import { ErrorPage } from "./components/ErrorPage";
import { GeoTiffLoader } from "./components/MapV2/utils/GeotiffLoader";
import { ResetPassword } from "./components/ResetPassword/ResetPassword";
import { TwoStepLogin } from "./components/TwoStepLogin/TwoStepLogin";
import { ROUTES } from "./routes";
import { AdminDashboardPage } from "./routes/admin/AdminDashboardPage";
import { AdminDataImportPage } from "./routes/admin/AdminDataImportPage";
import { AdminDjangoAdminPage } from "./routes/admin/AdminDjangoAdminPage";
import { AdminStatsPage } from "./routes/admin/AdminStatsPage";
import { AdminWorkflowEditorPage } from "./routes/admin/AdminWorkflowEditorPage";
import { AdminWorkflowsPage } from "./routes/admin/AdminWorkflowsPage";
import { DashboardPage } from "./routes/DashboardPage";
import { DataDownloadPage } from "./routes/DataDownloadPage";
import { DataUploadsPage } from "./routes/DataUploadsPage";
import { MainDashboardContainer } from "./routes/dashboards/MainDashboardPage";
import { LoginPageLayout } from "./routes/LoginPageLayout";
import { LogoutPage } from "./routes/LogoutPage";
import { MapPage } from "./routes/MapPage";
import { NonTemporaryUserGate } from "./routes/NonTemporaryUserGate";
import { NotificationChannelConfirmation } from "./routes/NotificationChannelConfirmation";
import { NotificationSettingsPage } from "./routes/NotificationSettingsPage";
import { SettingsHomePage } from "./routes/SettingsHomePage";
import { Licensing } from "./routes/static/Licensing";
import { PrivacyPolicy } from "./routes/static/PrivacyPolicy";
import { SupportPage } from "./routes/static/SupportPage";
import { TermsOfService } from "./routes/static/TermsOfService";
import "maplibre-gl/dist/maplibre-gl.css";
import { Settings as LuxonSettings } from "luxon";
import { EmissionsDashboardV2 } from "./components/dashboards/EmissionDashboardV2";
import { IPNotWhitelisted } from "./components/IPNotWhitelisted";
import { SSOComplete } from "./components/TwoStepLogin/SSOComplete";
import { UnmatchedData } from "./components/UnmatchedData";
import { AnalyticsPage } from "./routes/AnalyticsPage";
import { AdminViewAsCompanyPage } from "./routes/admin/AdminViewAsCompany";
import { SuperEmitterProgramDashboardPage } from "./routes/dashboards/SuperEmitterDashboardPage";
import { InfrastructurePageV2 } from "./routes/InfrastructurePageV2";
import { InfrastructureSitePage } from "./routes/InfrastructureSitePage";
import { MobileDevicesPage } from "./routes/MobileDevicesPage";
import { Aersage } from "./routes/static/AERSAGE";

// Set default timezone for system
LuxonSettings.defaultZone = "utc";

// Register data loaders for map
registerLoaders(GeoTiffLoader);

const ErrorBoundaryLayout = () => (
    <Sentry.ErrorBoundary fallback={<ErrorPage />}>
        <Outlet />
    </Sentry.ErrorBoundary>
);

const router = createBrowserRouter([
    {
        element: <ErrorBoundaryLayout />,
        children: [
            {
                path: ROUTES.LOGIN,
                element: (
                    <LoginPageLayout>
                        <TwoStepLogin />
                    </LoginPageLayout>
                ),
            },
            {
                path: ROUTES.IP_NOT_WHITELISTED,
                element: (
                    <LoginPageLayout>
                        <IPNotWhitelisted />
                    </LoginPageLayout>
                ),
            },
            {
                path: ROUTES.SSO_COMPLETE,
                element: (
                    <LoginPageLayout>
                        <SSOComplete />
                    </LoginPageLayout>
                ),
            },
            {
                path: ROUTES.PASSWORD_RESET,
                element: (
                    <LoginPageLayout>
                        <ResetPassword />
                    </LoginPageLayout>
                ),
            },
            {
                path: ROUTES.NOTIFICATION_CHANNEL_VALIDATION,
                element: (
                    <LoginPageLayout>
                        <NotificationChannelConfirmation />
                    </LoginPageLayout>
                ),
            },
            {
                path: ROUTES.LOGOUT,
                element: <LogoutPage />,
            },
            {
                path: ROUTES.ERROR_PAGE,
                element: <ErrorPage />,
            },
            {
                path: ROUTES.ADMIN_DASHBOARD,
                element: <AdminDashboardPage />,
                children: [
                    {
                        path: ROUTES.ADMIN_DATA_IMPORT,
                        element: <AdminDataImportPage />,
                    },
                    {
                        path: ROUTES.ADMIN_STATS,
                        element: <AdminStatsPage />,
                    },
                    {
                        path: ROUTES.ADMIN_DJANGO_ADMIN,
                        element: <AdminDjangoAdminPage />,
                    },
                    {
                        path: ROUTES.ADMIN_VIEW_AS_COMPANY,
                        element: <AdminViewAsCompanyPage />,
                    },
                    {
                        path: ROUTES.ADMIN_WORKFLOWS,
                        element: <AdminWorkflowsPage />,
                    },
                    {
                        path: ROUTES.ADMIN_WORKFLOW_EDITOR,
                        element: <AdminWorkflowEditorPage />,
                    },
                ],
            },
            {
                path: ROUTES.ROOT,
                element: <DashboardPage />,
                children: [
                    {
                        path: ROUTES.MAP,
                        element: <MapPage />,
                    },
                    {
                        path: ROUTES.INFRASTRUCURE,
                        element: <InfrastructurePageV2 />,
                    },
                    {
                        path: ROUTES.INFRASTRUCTURE_SITE,
                        element: <InfrastructureSitePage />,
                    },
                    {
                        path: ROUTES.DATAUPLOADS,
                        element: <DataUploadsPage />,
                    },
                    {
                        path: ROUTES.DATADOWNLOAD,
                        element: <DataDownloadPage />,
                    },
                    {
                        path: ROUTES.LICENSING,
                        element: <Licensing />,
                    },
                    {
                        path: ROUTES.SUPPORT,
                        element: <SupportPage />,
                    },
                    {
                        path: ROUTES.TERMS_OF_SERVICE,
                        element: <TermsOfService />,
                    },
                    {
                        path: ROUTES.PRIVACY_POLICY,
                        element: <PrivacyPolicy />,
                    },
                    {
                        path: ROUTES.AERSAGE,
                        element: <Aersage />,
                    },
                    {
                        path: ROUTES.SETTINGS_HOME,
                        element: (
                            <NonTemporaryUserGate>
                                <SettingsHomePage />
                            </NonTemporaryUserGate>
                        ),
                    },
                    {
                        path: ROUTES.SETTINGS_NOTIFICATIONS,
                        element: (
                            <NonTemporaryUserGate>
                                <NotificationSettingsPage />
                            </NonTemporaryUserGate>
                        ),
                    },
                    {
                        path: ROUTES.SETTINGS_MOBILE_DEVICES,
                        element: (
                            <NonTemporaryUserGate>
                                <MobileDevicesPage />
                            </NonTemporaryUserGate>
                        ),
                    },
                    {
                        path: ROUTES.ANALYTICS,
                        element: <AnalyticsPage />,
                    },
                    {
                        path: ROUTES.DASHBOARD,
                        element: <MainDashboardContainer />,
                        children: [
                            {
                                path: ROUTES.DASHBOARD_ALL,
                                element: (
                                    <EmissionsDashboardV2
                                        title="All Emissions"
                                        className="bg-neutral-600"
                                    />
                                ),
                            },
                            {
                                path: ROUTES.DASHBOARD_THIRD_PARTY,
                                element: (
                                    <EmissionsDashboardV2
                                        title="3rd Party Emissions"
                                        className="bg-ae-emission-3rd-party"
                                        emissionType="THIRD_PARTY"
                                    />
                                ),
                            },
                            {
                                path: ROUTES.DASHBOARD_SELF_REPORTED,
                                element: (
                                    <EmissionsDashboardV2
                                        title="Company Monitoring"
                                        className="bg-ae-emission-self-reported"
                                        emissionType="SELF_REPORTED"
                                    />
                                ),
                            },
                            {
                                path: ROUTES.DASHBOARD_UNMATCHED_DATA,
                                element: <UnmatchedData />,
                            },
                            {
                                path: ROUTES.DASHBOARD_EPA_SEP,
                                element: <SuperEmitterProgramDashboardPage />,
                            },
                        ],
                    },
                ],
            },
        ],
    },
]);

function App() {
    return (
        <>
            <CookieBanner />
            <RouterProvider router={router} />
        </>
    );
}

export default App;
